﻿import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  selectSelectedConnection,
  standardTypes,
  setStandardFilterTypes,
  selectStandardFilterTypes,
} from "@/features/connections/integrationSlice";
import { Container, IconButton } from "@mui/material";
import ShepTable from "@/components/GenericComponents/ShepTable";
import { ShepTooltip } from "@/components/GenericComponents/ShepComponents";
import { FilterDropDownType } from "@/utils/utils";
import {
  selectIsMappingClicked,
  setSelectedSchema,
  selectSelectedSchema,
  setIsMappingClicked,
} from "@/features/mapSchema/mapSchemaSlice";
import { resetArtifactPreview } from "@/features/connections/artifactPreviewSlice";
import EdiInboundMap from "./EdiInboundMap";
import EdiOutboundMap from "./EdiOutboundMap";
import axios from "@/features/axiosHelper";
import EditFillIcon from "@/assets/img/icons/Edit/Fill.svg?react";
import { useGridApiRef } from "@mui/x-data-grid-pro";

const Mapping = () => {
  const dispatch = useDispatch();
  const [referenceFilter, setReferenceFilter] = useState("");
  const apiRef = useGridApiRef();

  const selectedConnection = useSelector(selectSelectedConnection);

  const [schemaCollectionDocuments, setSchemaCollectionDocuments] = useState(
    []
  );

  const prepFilters = () => {
    dispatch(setStandardFilterTypes({ value: standardTypes }));
  };

  useEffect(() => {
    prepFilters();
    getSchemaCollectionDocuments();
    dispatch(setSelectedSchema([]));

    return () => {
      dispatch(resetArtifactPreview());
      dispatch(setIsMappingClicked({ isMappingClicked: false }));
    };
  }, []);

  const getSchemaCollectionDocuments = () => {
    axios
      .get("/connectionartifactschema", {
        params: {
          connectionId: selectedConnection.ConnectionId,
          isMapping: true,
        },
      })
      .then((response) => {
        //put it into a new List in redux and in AddEdit -> use the selector to read.
        setSchemaCollectionDocuments(response.data);
      })
      .catch((error) => {
        console.error("Error fetching data: ", error);
      });
  };

  const handleTextChange = (event) => {
    const referenceNumber = event.target.value;
    setReferenceFilter(referenceNumber);
  };

  const standardFilter = useSelector(selectStandardFilterTypes);

  const isMappingClicked = useSelector(selectIsMappingClicked);

  const filteredSchemaCollectionDocuments = useMemo(() => {
    let filtered = schemaCollectionDocuments;

    if (standardFilter && standardFilter.length > 0) {
      const checkedLabels = standardFilter
        .filter((item) => item.isChecked)
        .map((item) => item.label);

      if (
        checkedLabels.length > 0 &&
        checkedLabels.length !== standardFilter.length
      ) {
        filtered = filtered.filter((row) =>
          checkedLabels.includes(row.Standard)
        );
      }
    }

    const searchText = (referenceFilter ?? "").trim().toLowerCase();
    if (searchText !== "") {
      filtered = filtered.filter((row) => {
        const fieldsToSearch = [
          row.Name, // e.g 1 - EDISAMPLE1
          row.SchemaName, // e.g 2 - O855 - second
          row.Document, //e.g O855
          row.Standard, //e.g X12EDI 
          row.Direction, // Inbound or Outbound
          row.VariantName, //e.g SOPI
          row.DocumentDescription, //e.g Customer Invoice 
        ];
        return fieldsToSearch.some(
          (field) =>
            field && field.toString().toLowerCase().includes(searchText)
        );
      });
    }

    return filtered;
  }, [schemaCollectionDocuments, standardFilter, referenceFilter]);

  const schema = [
    {
      id: 0,
      label: "Standard",
      type: FilterDropDownType.CheckboxList,
      cascading: true,
      includingAllCheckBox: true,
      childLabel: "Select Standard",
      setValue: setStandardFilterTypes,
      readVal: selectStandardFilterTypes,
      labelKey: "label",
      uniqueKey: "key",
    },
  ];

  const onEditIconClick = (event, row) => {
    event.stopPropagation();
    if (!row) return;
    dispatch(setSelectedSchema({ selectedSchema: row }));
    dispatch(setIsMappingClicked({ isMappingClicked: true }));
  };

  const columns = [
    {
      field: "Name",
      headerName: "Name",
      width: 200,
    },
    {
      field: "SchemaName",
      headerName: "Schema Name",
      width: 400,
    },
    {
      field: "Document",
      headerName: "Document",
      width: 200,
    },
    {
      field: "Standard",
      headerName: "Standard",
      width: 200,
    },
    {
      field: "Direction",
      headerName: "Direction",
      width: 200,
    },
    {
      field: "VariantName",
      headerName: "Variant",
      width: 200,
    },
    {
      field: "DocumentDescription",
      headerName: "Document Description",
      width: 300,
    },
    {
      field: "mapAction",
      headerName: "",
      width: 60,
      sortable: false,
      renderCell: (params) => (
        <ShepTooltip title="Edit Mapping">
          <IconButton
            className="workflow-row"
            size="small"
            onClick={(e) => onEditIconClick(e, params.row)}
            disableRipple
          >
            <EditFillIcon />
          </IconButton>
        </ShepTooltip>
      ),
    },
  ];

  const [rowSelectionModel, setRowSelectionModel] = useState([]);

  const handleRowSelection = (newRowSelectionModel) => {
    setRowSelectionModel(newRowSelectionModel);

    const selectedData = schemaCollectionDocuments.find(
      (row) => row.EndpointSchemaId === newRowSelectionModel[0]
    );

    if (selectedData !== null && selectedData !== undefined) {
      dispatch(setSelectedSchema({ selectedSchema: selectedData }));
    } else {
      dispatch(setSelectedSchema([]));
    }
  };

  const selectedSchema = useSelector(selectSelectedSchema);

  return (
    <Container
      className="content-container"
      style={{ padding: "0px" }}
      sx={{
        display: "flex",
        flexDirection: "column",
        flex: "1 1 auto",
        minHeight: 0,
      }}
    >
      {isMappingClicked ? (
        selectedSchema?.Direction === "Outbound" ? (
          <EdiOutboundMap />
        ) : (
          <EdiInboundMap />
        )
      ) : (
        <ShepTable
          tableTitle="Mapping"
          columnTitles={columns}
          dataRows={filteredSchemaCollectionDocuments}
          schemas={schema}
          searchValue={referenceFilter}
          selectedRow={rowSelectionModel}
          handleSearchChange={handleTextChange}
          handleSelectedRowChange={handleRowSelection}
          getRowId={(row) => row.EndpointSchemaId}
          apiRef={apiRef}
        />
      )}
    </Container>
  );
};
export default Mapping;
